import {
  ECouncilAgendaItemType,
  ICouncilAgendaItem,
} from "dto/Council/ICouncil";

// Numeracja porządku obrad — wierny port algorytmu z Magistratu
// (Kwantum.Cases.UI/CouncilSessionAgenda/ViewModel/AgendaViewModel.cs:
//  NewElementExecuted, NewChildElementExecuted, MoveUp/MoveDownExecuted,
//  RemoveRecurency, Renumerate/renumerate).
//
// Zasady:
//   DODANIE KORZENIA:    Order = (liczba korzeni + 1)
//   DODANIE PODRZĘDNEGO: Order = parent.Order + "." + (liczba dzieci + 1)
//   USUNIĘCIE:           usuń całe poddrzewo → renumeruj całe drzewo
//   GÓRA/DÓŁ:            zamień Order z sąsiadem o tym samym ParentId → renumeruj całe drzewo
//   RENUMERACJA:         DFS pre-order po korzeniach posortowanych wg orderInt;
//                        kolejne 1..n, dziecko = parentOrder + "." + k
//
// Lista jest płaska (hierarchię niesie ParentId) — dokładnie jak kontrakt BE.

/** Ostatni segment ścieżki „1.2.3" — pozycja wśród rodzeństwa. */
export const orderInt = (item: Pick<ICouncilAgendaItem, "Order">): number => {
  if (!item.Order) return 0;

  const last = item.Order.split(".").pop();
  const parsed = Number.parseInt(last ?? "", 10);

  return Number.isNaN(parsed) ? 0 : parsed;
};

const childrenOf = (items: ICouncilAgendaItem[], parentId: string | null) =>
  items
    .filter((x) => (x.ParentId ?? null) === parentId)
    .sort((a, b) => orderInt(a) - orderInt(b));

/**
 * Przelicza Order całego drzewa. Zwraca NOWĄ tablicę w kolejności DFS pre-order,
 * gotową do wyświetlenia (rodzic zawsze przed swoimi dziećmi).
 */
export const renumerate = (items: ICouncilAgendaItem[]): ICouncilAgendaItem[] => {
  const result: ICouncilAgendaItem[] = [];

  const walk = (parentId: string | null, parentOrder: string | null) => {
    const children = childrenOf(items, parentId);

    children.forEach((child, index) => {
      const order = parentOrder ? `${parentOrder}.${index + 1}` : `${index + 1}`;
      const next = { ...child, Order: order };

      result.push(next);
      walk(next.Id, order);
    });
  };

  walk(null, null);

  // Pozycje osierocone (rodzic zniknął) — dokładamy na koniec, żeby żadna nie wyparowała.
  const seen = new Set(result.map((x) => x.Id));
  items.filter((x) => !seen.has(x.Id)).forEach((x) => result.push(x));

  return result;
};

/** Nowy punkt główny — na końcu listy korzeni. */
export const createRootItem = (
  items: ICouncilAgendaItem[],
  id: string,
): ICouncilAgendaItem => ({
  Id: id,
  ParentId: null,
  Order: `${items.filter((x) => !x.ParentId).length + 1}`,
  Type: ECouncilAgendaItemType.Description,
  Title: "",
  Description: null,
  IsActive: false,
  VotingType: 0,
  VotingResult: 0,
  HasActions: false,
  HasVotings: false,
  Attachments: [],
});

/** Nowy punkt podrzędny — na końcu listy dzieci wskazanego punktu. */
export const createChildItem = (
  items: ICouncilAgendaItem[],
  parent: ICouncilAgendaItem,
  id: string,
): ICouncilAgendaItem => ({
  ...createRootItem([], id),
  ParentId: parent.Id,
  Order: `${parent.Order}.${items.filter((x) => x.ParentId === parent.Id).length + 1}`,
});

/** Usuwa punkt razem z całym poddrzewem, po czym renumeruje drzewo. */
export const removeItemRecursive = (
  items: ICouncilAgendaItem[],
  id: string,
): ICouncilAgendaItem[] => {
  const toRemove = new Set<string>();

  const collect = (itemId: string) => {
    toRemove.add(itemId);
    items.filter((x) => x.ParentId === itemId).forEach((x) => collect(x.Id));
  };

  collect(id);

  return renumerate(items.filter((x) => !toRemove.has(x.Id)));
};

/**
 * Przesuwa punkt w obrębie rodzeństwa (bez zmiany poziomu i rodzica) — zamiana
 * Order z bezpośrednim sąsiadem, jak w Magistracie, po czym renumeracja.
 */
export const moveItem = (
  items: ICouncilAgendaItem[],
  id: string,
  direction: "up" | "down",
): ICouncilAgendaItem[] => {
  const item = items.find((x) => x.Id === id);
  if (!item) return items;

  const siblings = items.filter(
    (x) => (x.ParentId ?? null) === (item.ParentId ?? null) && x.Id !== item.Id,
  );

  const candidates =
    direction === "up"
      ? siblings.filter((x) => orderInt(x) < orderInt(item)).sort((a, b) => orderInt(a) - orderInt(b))
      : siblings.filter((x) => orderInt(x) > orderInt(item)).sort((a, b) => orderInt(a) - orderInt(b));

  const neighbour =
    direction === "up" ? candidates[candidates.length - 1] : candidates[0];

  if (!neighbour) return items;

  const swapped = items.map((x) => {
    if (x.Id === item.Id) return { ...x, Order: neighbour.Order };
    if (x.Id === neighbour.Id) return { ...x, Order: item.Order };
    return x;
  });

  return renumerate(swapped);
};

/** Poziom zagnieżdżenia (0 = korzeń) — do wcięcia w drzewie. */
export const levelOf = (item: ICouncilAgendaItem): number =>
  item.Order ? item.Order.split(".").length - 1 : 0;

/**
 * Domyślna struktura pustego porządku obrad — jak w Magistracie
 * (AgendaViewModel.OnLoaded): pusty punkt 1 do wypełnienia + statyczny punkt 2.
 */
export const buildDefaultAgenda = (
  newId: () => string,
): ICouncilAgendaItem[] => {
  const first = createRootItem([], newId());
  const second = {
    ...createRootItem([first], newId()),
    Type: ECouncilAgendaItemType.StaticApproveAgenda,
    Title: "Przedstawienie porządku obrad",
  };

  return [first, second];
};

/** Nazwa punktu na liście — pusty tytuł zastępujemy podpowiedzią. */
export const itemLabel = (item: ICouncilAgendaItem): string =>
  item.Title?.trim() ? item.Title : "";
